import { useRef, useEffect, useState } from 'react';
import {
  FaClipboardCheck,
  FaHeadset,
  FaFolderOpen,
  FaTasks,
  FaGlobe,
  FaChevronLeft,
  FaChevronRight,
  FaCog
} from 'react-icons/fa';
import './QuickActions.css';

const quickActions = [
  { name: 'Monday.com', icon: FaTasks, url: 'https://monday.com' },
  { name: 'QMS', icon: FaClipboardCheck, url: '/qms' },
  { name: 'Aquadot', icon: FaFolderOpen, url: 'https://aquadot.projectgreen.mt/efile/' },
  { name: 'Resources', icon: FaCog, url: 'https://govmt.sharepoint.com/:f:/r/sites/PG-CentralResourceLibrary/Shared%20Documents/Central%20Resource%20Library?csf=1&web=1&e=sghwU5' },
  { name: 'Talexio', icon: FaGlobe, url: 'https://projectgreen.talexiohr.com/login' },
  { name: 'IT Support', icon: FaHeadset, url: 'https://wkf.ms/4cRAM1m' },
];

const CARD_WIDTH = 132;
const CARD_GAP = 10;
const MOBILE_BREAKPOINT = 768;

// Fixed 2-per-page carousel — mobile only. Desktop/monitor keeps its
// original static 6-across grid below, untouched.
const MOBILE_VISIBLE_COUNT = 2;

const QuickActions = () => {
  const wrapperRef = useRef(null);
  const observerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const [index, setIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);

    const handleChange = (e) => {
      setIsMobile(e.matches);
      setIndex(0);
    };

    setIsMobile(mql.matches);

    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }
    // Safari < 14 fallback
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, []);

  useEffect(() => {
    const el = observerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.classList.add('visible');
      },
      { threshold: 0.1, rootMargin: '0px 0px -120px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const visible = isMobile
    ? Math.min(MOBILE_VISIBLE_COUNT, quickActions.length)
    : quickActions.length;
  const maxIndex = Math.max(quickActions.length - visible, 0);

  const slide = (dir) => {
    if (animating) return;
    const next = Math.min(Math.max(index + dir, 0), maxIndex);
    if (next === index) return;
    setAnimating(true);
    setIndex(next);
    setTimeout(() => setAnimating(false), 350);
  };

  const canPrev = index > 0;
  const canNext = index < maxIndex;
  const offset = index * (CARD_WIDTH + CARD_GAP);

  // Desktop/monitor — original static grid, unchanged.
  if (!isMobile) {
    return (
      <div
        className="quick-actions quick-actions--desktop"
        ref={(el) => {
          wrapperRef.current = el;
          observerRef.current = el;
        }}
      >
        {quickActions.map((action, i) => (
          <a
            key={i}
            href={action.url}
            target="_blank"
            rel="noopener noreferrer"
            className="quick-action-link external"
          >
            <button className="quick-action-btn" type="button">
              <action.icon className="quick-action-icon" />
              <span className="quick-action-name">{action.name}</span>
            </button>
          </a>
        ))}
      </div>
    );
  }

  // Mobile — 2-per-page carousel.
  return (
    <div
      className="quick-actions quick-actions--carousel"
      ref={(el) => {
        wrapperRef.current = el;
        observerRef.current = el;
      }}
    >
      <button
        className={`carousel-arrow carousel-arrow--left ${!canPrev ? 'disabled' : ''}`}
        onClick={() => slide(-1)}
        aria-label="Previous"
        type="button"
      >
        <FaChevronLeft />
      </button>

      <div
        className="carousel-viewport"
        style={{ width: visible * CARD_WIDTH + (visible - 1) * CARD_GAP }}
      >
        <div
          className="carousel-track"
          style={{ transform: `translateX(-${offset}px)` }}
        >
          {quickActions.map((action, i) => (
            <a
              key={i}
              href={action.url}
              target="_blank"
              rel="noopener noreferrer"
              className="quick-action-link external carousel-item"
            >
              <button className="quick-action-btn" type="button">
                <action.icon className="quick-action-icon" />
                <span className="quick-action-name">{action.name}</span>
              </button>
            </a>
          ))}
        </div>
      </div>

      <button
        className={`carousel-arrow carousel-arrow--right ${!canNext ? 'disabled' : ''}`}
        onClick={() => slide(1)}
        aria-label="Next"
        type="button"
      >
        <FaChevronRight />
      </button>

      {maxIndex > 0 && (
        <div className="carousel-dots">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              className={`carousel-dot ${i === index ? 'active' : ''}`}
              onClick={() => setIndex(i)}
              aria-label={`Page ${i + 1}`}
              type="button"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default QuickActions;